import {
  AddRestrictionGroupsResponse,
  Exception,
  FetchRestrictedGroupsResponse,
  FetchRestrictedWordsResponse,
  RestrictedGroup,
  RestrictedWord
} from './restrictedWordsTypes';
import { autoMapper } from 'utils/auto-mapper';
import { GLOBAL_GROUP_ID, GLOBAL_GROUP_NAME } from './restrictedWordsConstants';
import { FilterOption, Suggestion } from './components/Inputs/inputTypes';

export function parseWords(data: FetchRestrictedWordsResponse): Array<RestrictedWord> {
  return autoMapper(data, {
    text: 'value',
    id: 'id',
    policy: 'policy',
    groups_count: 'groupsCount'
  });
}

export function parseGroups(data: FetchRestrictedGroupsResponse): Array<RestrictedGroup> {
  return autoMapper(data, {
    id: 'id',
    name: 'value',
    words_count: 'wordsCount'
  });
}

export function getGlobalGroup(count = 0): RestrictedGroup {
  return {
    id: GLOBAL_GROUP_ID,
    value: GLOBAL_GROUP_NAME,
    wordsCount: count
  };
}

export function isGlobalGroup(group: RestrictedGroup | number): boolean {
  if (typeof group === 'number') {
    return group === GLOBAL_GROUP_ID;
  }

  return group.id === GLOBAL_GROUP_ID;
}

export const defaultFilterOptions: FilterOption<Suggestion> = (
  option: Suggestion,
  input: string
) => {
  if (!input) {
    return true;
  }

  const inputs = input
    .split(',')
    .map(input => input.trim().toLowerCase())
    .filter(input => input.length > 0);

  return inputs.some(input => option.value.toLowerCase().includes(input));
};

export const parseWordSettings = (data: FetchRestrictedWordsResponse) => {
  return autoMapper(data, {
    text: 'value',
    id: 'id',
    policy: 'policy',
    groups_count: 'groupsCount',
    exceptions: 'exceptions',
    restrictionGroups: 'restrictionGroups'
  });
};

export const sortByAssignedAndAlphabetically = (groups: { [id: string]: RestrictedGroup }) => {
  // organizes groups into two:
  // 1. assigned groups at the top (in alphabetical order)
  // 2. unassigned groups below (also in alphabetical order)
  return Object.values(groups).sort((a, b) => {
    if (b.isAssigned && !a.isAssigned) {
      return 1;
    } else if (a.isAssigned && !b.isAssigned) {
      return -1;
    } else {
      return a.value.localeCompare(b.value);
    }
  });
};

export const mergeAndSortUniqueGroups = (
  assignedGroups: Array<RestrictedGroup>,
  allGroups: Array<RestrictedGroup>
): Array<RestrictedGroup> => {
  const mergedGroups = [...assignedGroups, ...allGroups];
  const uniqueMap: { [id: string]: RestrictedGroup } = {};

  for (const group of mergedGroups) {
    if (!uniqueMap[group.id]) {
      uniqueMap[group.id] = { ...group };
    } else {
      uniqueMap[group.id].isAssigned = true;
    }
  }

  return sortByAssignedAndAlphabetically(uniqueMap);
};

export const createExceptionObject = (exceptions: Array<string>) =>
  exceptions?.map(exception => ({ value: exception }));

export function parseExceptions(data: Array<Exception> | Array<string>) {
  return data?.map(obj => obj.value.trim());
}

export const compareExceptions = (arr1: Array<Exception>, arr2: Array<Exception>) => {
  return (
    arr1?.length !== arr2.length || arr1?.some((item, index) => item.value !== arr2[index].value)
  );
};

export const updateCreatedRestcitionGroupsIdForUndo = (
  inputData: Array<RestrictedGroup>,
  response: AddRestrictionGroupsResponse
) => {
  const responseMap = new Map();
  response.forEach(({ id, name }) => {
    responseMap.set(name, id);
  });
  const updatedData = inputData.map(item => {
    const id = responseMap.get(item.value);
    if (id) {
      return { ...item, id };
    }
    return item;
  });

  return updatedData;
};

export function localCompare(a: RestrictedWord, b: RestrictedWord) {
  return a.value.toLowerCase().localeCompare(b.value.toLowerCase());
}
